/**
 * Format a Date (or now) as YYYY-MM-DD in the local timezone.
 * Avoid Date#toISOString(), which uses UTC and can show the previous calendar day
 * for timezones ahead of UTC (e.g. Asia/Dubai, Asia/Kolkata).
 */
export function formatLocalDate(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return formatLocalDate(new Date());
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert an API/date value to an <input type="date"> value (YYYY-MM-DD, local).
 * Prefer the calendar date portion of ISO strings so midnight UTC does not shift the day.
 */
export function toInputDate(value) {
  if (!value) return formatLocalDate();
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  return formatLocalDate(value);
}

export function addLocalDays(days, from = new Date()) {
  const date = from instanceof Date ? new Date(from.getTime()) : new Date(from);
  date.setDate(date.getDate() + Number(days || 0));
  return formatLocalDate(date);
}

/**
 * Parse YYYY-MM-DD (or Date) into local calendar parts for API Month/Day/Year fields.
 * Do not use `new Date('YYYY-MM-DD')` + getDate() — that is UTC midnight and shifts the day.
 */
export function parseLocalDateParts(value) {
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
    }
  }
  if (value && typeof value === 'object' && value.Day != null && value.Month != null && value.Year != null) {
    return {
      year: Number(value.Year),
      month: Number(value.Month),
      day: Number(value.Day),
    };
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return parseLocalDateParts(formatLocalDate());
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

/**
 * Normalize API/order date values to YYYY-MM-DD for inputs and SaveOrder payloads.
 */
export function orderDateToInputValue(value) {
  if (!value) return formatLocalDate();
  if (typeof value === 'object' && value.Day != null && value.Month != null && value.Year != null) {
    const year = Number(value.Year);
    const month = String(value.Month).padStart(2, '0');
    const day = String(value.Day).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return toInputDate(value);
}

/**
 * Add PMonth/PDay/PYear and DMonth/DDay/DYear fields expected by SaveOrder API.
 */
export function applySaveOrderDateFields(order) {
  const pickupInput = orderDateToInputValue(order?.PickupDate ?? order?.pickupDate);
  const deliveryInput = orderDateToInputValue(order?.DeliveryDate ?? order?.deliveryDate);
  const pickupParts = parseLocalDateParts(pickupInput);
  const deliveryParts = parseLocalDateParts(deliveryInput);

  return {
    ...order,
    PickupDate: pickupInput,
    DeliveryDate: deliveryInput,
    PMonth: pickupParts.month,
    PDay: pickupParts.day,
    PYear: pickupParts.year,
    DMonth: deliveryParts.month,
    DDay: deliveryParts.day,
    DYear: deliveryParts.year,
  };
}
